import React, { useCallback, useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "components/common/TableContainer";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
} from "reactstrap";
import { Enquiry } from "features/Enquiries/types/enquiry.types";
import {
  deleteEnquiry,
  getEnquiries,
} from "features/Enquiries/services/EnquiryService";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import {
  EnquiryIntent,
  EnquiryIntentLabelMap,
  EnquiryStatus,
  enquiryStatusMap,
} from "features/Enquiries/constants/enquiry.constants";
import { ColumnDef } from "@tanstack/react-table";
import StatusBadge from "components/common/StatusBadge";
import { CodeTitleColumns, IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { priorityMap } from "features/Meta/constants/meta.constants";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import EnquiryListFilters from "./EnquiryListFilters";
import EnquiryActionsDropdown from "features/Enquiries/components/EnquiryActionsDropdown";
import EnquiryAssigneesAvatarGroup from "features/Enquiries/components/EnquiryAssigneesAvatarGroup";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";

const EnquiryList = () => {
  document.title = getDocumentTitle("Enquiries");
  const { can } = useAccess();
  const canReadEnquiry = can(Permissions.ENQUIRY.READ);
  const canUpdateEnquiry = can(Permissions.ENQUIRY.UPDATE);
  const canDeleteEnquiry = can(Permissions.ENQUIRY.DELETE);
  const canReadSiteVisit = can(Permissions.SITE_VISIT.READ);
  const canCreateSiteVisit = can(Permissions.SITE_VISIT.CREATE);
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);

  const tableRef = useRef<() => void>(null);
  const [enquiry, setEnquiry] = useState<Enquiry | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormEnquiry, setAssignFormEnquiry] = useState<Enquiry | null>(null);
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormEnquiry(null);
    setAssignFormIsReassign(false);
  }, []);

  const openAssign = useCallback((record: Enquiry) => {
    setAssignFormIsReassign(false);
    setAssignFormEnquiry(record);
    setAssignFormOpen(true);
  }, []);

  const openReassign = useCallback((record: Enquiry) => {
    setAssignFormIsReassign(true);
    setAssignFormEnquiry(record);
    setAssignFormOpen(true);
  }, []);

  const [statusFilter, setStatusFilter] = useState("");
  const [intentFilter, setIntentFilter] = useState("");
  const [clientIdFilter, setClientIdFilter] = useState<number | null>(null);
  const requestParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      enquiryIntent: intentFilter || undefined,
      clientId: clientIdFilter ?? undefined,
    }),
    [clientIdFilter, statusFilter, intentFilter],
  );

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (statusFilter) n += 1;
    if (intentFilter) n += 1;
    if (clientIdFilter != null) n += 1;
    return n;
  }, [clientIdFilter, statusFilter, intentFilter]);

  const clearListFilters = useCallback(() => {
    setStatusFilter("");
    setIntentFilter("");
    setClientIdFilter(null);
  }, []);

  const onClickDelete = useCallback((record: Enquiry) => {
    setEnquiry(record);
    setDeleteModal(true);
  }, []);

  const handleDelete = async () => {
    if (enquiry) {
      try {
        const response = await deleteEnquiry(enquiry.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err: any) {
        toast.error(err.response.data.message);
      }
    }
  };

  const columns: ColumnDef<Enquiry>[] = useMemo(
    () => [
      IndexColumn<Enquiry>(),
      ...CodeTitleColumns<Enquiry>({
        titleHeader: "Enquiry",
        accessorKey: "code",
        resolve: (r) => ({
          code: r.code,
          title: r.title?.trim(),
          to: canReadEnquiry ? `/enquiry-management/enquiries/${r.id}` : null,
        }),
      }),
      {
        header: "Intent",
        id: "enquiryIntent",
        accessorFn: (row) =>
          EnquiryIntentLabelMap[row.enquiryIntent as EnquiryIntent] ??
          row.enquiryIntent ??
          "",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const intent = row.original.enquiryIntent;
          const label =
            EnquiryIntentLabelMap[intent as EnquiryIntent] ?? intent ?? "—";
          return <span className="text-body">{label}</span>;
        },
      },
      {
        header: "Priority",
        accessorKey: "priority.name",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <StatusBadge value={row.original.priorityId} map={priorityMap} />
          );
        },
      },

      {
        header: "Assigned",
        id: "assignedUsers",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const isTerminal =
            record.status === EnquiryStatus.CONVERTED ||
            record.status === EnquiryStatus.CANCELLED;
          return (
            <EnquiryAssigneesAvatarGroup
              enquiry={record}
              workAssignments={record.workAssignments}
              canReadDetail={canReadEnquiry}
              canAssignMore={canCreateAssignment && !isTerminal}
              onOpenAssign={openAssign}
            />
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const val = row.original.status;
          return <StatusBadge value={val} map={enquiryStatusMap} />;
        },
      },

      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at} />;
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          return (
            <EnquiryActionsDropdown
              record={record}
              canReadEnquiry={canReadEnquiry}
              canUpdateEnquiry={canUpdateEnquiry}
              canDeleteEnquiry={canDeleteEnquiry}
              canReadSiteVisit={canReadSiteVisit}
              canCreateSiteVisit={canCreateSiteVisit}
              onDelete={onClickDelete}
              onAssign={canCreateAssignment ? openAssign : undefined}
              onReassign={canUpdateAssignment ? openReassign : undefined}
            />
          );
        },
      },
    ],
    [onClickDelete, openAssign, openReassign, canReadEnquiry, canUpdateEnquiry, canDeleteEnquiry, canReadSiteVisit, canCreateSiteVisit, canCreateAssignment, canUpdateAssignment],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />
      <WorkAssignmentForm
        show={assignFormOpen && assignFormEnquiry != null}
        sourceType={WorkAssignmentSourceType.ENQUIRY}
        sourceId={assignFormEnquiry?.id ?? 0}
        sourceLabel={
          assignFormEnquiry
            ? assignFormEnquiry.code
              ? `Enquiry ${assignFormEnquiry.code}`
              : assignFormEnquiry.title
                ? `Enquiry: ${assignFormEnquiry.title}`
                : `Enquiry #${assignFormEnquiry.id}`
            : ""
        }
        isReassign={assignFormIsReassign}
        onCloseClick={closeAssignmentForm}
        onSuccess={() => { tableRef.current?.(); }}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Enquiries" pageTitle="Enquiry Management" />
          <Card>
            <CardHeader className="d-flex justify-content-between">
              <h5 className="card-title mb-0">Enquiry List</h5>
              {can(Permissions.ENQUIRY.CREATE) ? (
                <Link
                  to="/enquiry-management/enquiries/create"
                  className="btn btn-primary"
                >
                  <i className="ri-add-line align-bottom me-1"></i> Add Enquiry
                </Link>
              ) : null}
            </CardHeader>
            <CardBody className="pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getEnquiries}
                requestParams={requestParams}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                toolbarRight={
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <EnquiryListFilters
                      status={statusFilter}
                      onStatusChange={setStatusFilter}
                      intent={intentFilter}
                      onIntentChange={setIntentFilter}
                      clientId={clientIdFilter}
                      onClientIdChange={setClientIdFilter}
                    />
                    {filtersBadgeCount > 0 && (
                      <button
                        type="button"
                        className="btn btn-soft-danger d-flex align-items-center gap-1"
                        onClick={clearListFilters}
                      >
                        <i className="ri-close-circle-line" />
                        Clear
                      </button>
                    )}
                  </div>
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EnquiryList;
